import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { collection, getDocs, query, limit, where } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { Shop, Product } from "@/src/types";
import { ShopCard } from "../components/ShopCard";
import { ProductCard } from "../components/ProductCard";
import { Loader2, Search, MapPin, Tag, Package, Store, Filter, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CATEGORIES, MALL_ZONES } from "../constants";
import { LocationSelector } from "../components/LocationSelector";
import { Separator } from "@/components/ui/separator";

// Helper to determine continent (mock logic for demo)
const getContinentForCountry = (country: string): string => {
  const normalized = country.toLowerCase().trim();
  
  const americaCountries = [
    "canadá", "canada", "eeuu", "estados unidos", "usa", "united states", "united state", "ee.uu", "méxico", "mexico",
    "guatemala", "belice", "el salvador", "honduras", "nicaragua", "costa rica", "panamá", "panama",
    "cuba", "república dominicana", "republica dominicana", "haití", "haiti", "jamaica", "puerto rico", "bahamas", 
    "trinidad y tobago", "barbados", "santa lucía", "granada", "san vicente y las granadinas", 
    "antigua y barbuda", "san cristóbal y nieves", "dominica", "colombia", "venezuela", 
    "ecuador", "perú", "peru", "bolivia", "brasil", "brazil", "paraguay", "uruguay", "argentina", "chile", 
    "guyana", "surinam", "guyana francesa"
  ];

  const europeCountries = [
    "españa", "espana", "spain", "francia", "france", "italia", "italy", "alemania", "germany", "reino unido", "uk", "united kingdom", "portugal", "países bajos", "netherlands", 
    "bélgica", "belgium", "suiza", "switzerland", "austria", "grecia", "greece", "suecia", "sweden", "noruega", "norway", "dinamarca", "denmark", 
    "finlandia", "finland", "irlanda", "ireland", "polonia", "poland", "república checa", "hungría", "rumanía", 
    "bulgaria", "croacia", "eslovaquia", "eslovenia", "estonia", "letonia", 
    "lituania", "luxemburgo", "malta", "chipre"
  ];

  if (americaCountries.some(c => normalized.includes(c))) return "América";
  if (europeCountries.some(c => normalized.includes(c))) return "Europa";
  
  return "Otro";
};

/**
 * Robustly standardizes country names to avoid duplicates in the UI.
 * Maps all variations of USA to "Estados Unidos".
 */
const normalizeCountry = (country: string): string => {
  const normalized = country.trim().toLowerCase();
  
  const usaVariants = [
    'usa', 'u.s.a.', 'u.s.a', 'us', 'u.s.', 'u.s', 
    'eeuu', 'ee.uu.', 'ee.uu', 'e.e.u.u.', 'e.e.u.u',
    'united states', 'united state', 'united states of america',
    'estados unidos', 'estados unido', 'estados unidos de america', 'estados unidos de américa'
  ];

  if (usaVariants.includes(normalized)) {
    return "Estados Unidos";
  }

  // General Title Case for other countries
  return country.trim().split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

/**
 * Robustly standardizes city names (e.g., "Madrid").
 */
const normalizeCity = (city: string): string => {
  const normalized = city.trim().toLowerCase();
  
  if (normalized === 'madrid') {
    return "Madrid";
  }

  return city.trim().split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

export function ShopsPage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<{ continent: string; country: string; city: string } | null>(null);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [zoneFilter, setZoneFilter] = useState<string | null>(null);
  const [shopNameSearch, setShopNameSearch] = useState("");
  const [zoneSearch, setZoneSearch] = useState("");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [businessType, setBusinessType] = useState<"products" | "services" | "both" | "all">("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shopsSnap, productsSnap] = await Promise.all([
          getDocs(collection(firestoreDb, "shops")),
          getDocs(collection(firestoreDb, "products"))
        ]);
        
        setShops(shopsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Shop)));
        setAllProducts(productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product)));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const availableLocations = useMemo(() => {
    const locations: Record<string, { country: string; continent: string; cities: Set<string> }> = {};
    
    shops.forEach(shop => {
      const country = normalizeCountry(shop.location.country);
      const city = normalizeCity(shop.location.city);
      const continent = getContinentForCountry(country);
      
      if (!locations[country]) {
        locations[country] = { country, continent, cities: new Set() };
      }
      locations[country].cities.add(city);
    });
    
    return Object.values(locations).map(loc => ({
      ...loc,
      cities: Array.from(loc.cities)
    }));
  }, [shops]);

  const sortedAndFilteredShops = useMemo(() => {
    let result = [...shops];

    // Filter by location modal selection
    if (selectedLocation) {
      if (selectedLocation.country) {
        result = result.filter(s => normalizeCountry(s.location.country) === selectedLocation.country);
      }
      if (selectedLocation.city && selectedLocation.city !== "Todas las ciudades") {
        result = result.filter(s => normalizeCity(s.location.city) === selectedLocation.city);
      }
    }

    // Category filter
    if (categoryFilter) {
      result = result.filter(s => s.categories.includes(categoryFilter));
    }
    
    // Zone (Mall Zone) filter
    if (zoneFilter) {
      result = result.filter(s => s.mallZone === zoneFilter);
    }

    // Name search
    if (shopNameSearch) {
      result = result.filter(s => s.name.toLowerCase().includes(shopNameSearch.toLowerCase()));
    }

    // Zone search
    if (zoneSearch) {
      result = result.filter(s => 
        s.location.address?.toLowerCase().includes(zoneSearch.toLowerCase()) ||
        s.location.city.toLowerCase().includes(zoneSearch.toLowerCase())
      );
    }

    // Business Type
    if (businessType !== "all") {
      result = result.filter(s => s.type === businessType || s.type === "both");
    }

    // Price filtering (based on its products)
    if (priceRange.min || priceRange.max) {
      result = result.filter(shop => {
        const shopProducts = allProducts.filter(p => p.shopId === shop.id);
        if (shopProducts.length === 0) return false;
        
        return shopProducts.some(p => {
          const min = priceRange.min ? parseFloat(priceRange.min) : 0;
          const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
          return p.price >= min && p.price <= max;
        });
      });
    }

    // PREMIUM PRIORITIZATION
    return result.sort((a, b) => {
      if (a.plan === "premium" && b.plan !== "premium") return -1;
      if (b.plan === "premium" && a.plan !== "premium") return 1;
      return 0;
    });
  }, [shops, selectedLocation, categoryFilter, shopNameSearch, businessType, priceRange, allProducts]);

  return (
    <div className="container py-12 px-4 md:px-8">
      <LocationSelector 
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onSelect={(loc) => setSelectedLocation(loc)}
        availableLocations={availableLocations}
      />

      <div className="flex flex-col gap-8 mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Explorar Tiendas</h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {selectedLocation ? (
                <span>
                  {selectedLocation.city === "Todas las ciudades" ? "" : `${selectedLocation.city}, `}
                  {selectedLocation.country} ({selectedLocation.continent})
                </span>
              ) : (
                <span>Todas las ubicaciones</span>
              )}
              <Button variant="link" size="sm" className="h-auto p-0 text-primary" onClick={() => setIsLocationModalOpen(true)}>
                Cambiar
              </Button>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full md:w-auto gap-2"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtros {showAdvancedFilters ? "Básicos" : "Avanzados"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por nombre..." 
              className="pl-10 h-10 rounded-xl"
              value={shopNameSearch}
              onChange={(e) => setShopNameSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Badge 
              variant={categoryFilter === null ? "default" : "outline"}
              className="cursor-pointer h-10 px-4 rounded-xl"
              onClick={() => setCategoryFilter(null)}
            >
              Todas
            </Badge>
            <select 
              className="h-10 text-sm bg-background border rounded-xl px-4 outline-none flex-1 min-w-[150px]"
              onChange={(e) => setCategoryFilter(e.target.value || null)}
              value={categoryFilter || ""}
            >
              <option value="">Categorías...</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <select 
              className="h-10 text-sm bg-background border rounded-xl px-4 outline-none flex-1 min-w-[150px]"
              onChange={(e) => setZoneFilter(e.target.value || null)}
              value={zoneFilter || ""}
            >
              <option value="">Zonas del Mall...</option>
              {MALL_ZONES.map(zone => (
                <option key={zone.id} value={zone.id}>{zone.label}</option>
              ))}
            </select>
          </div>

          {showAdvancedFilters && (
            <>
              <div className="flex gap-2">
                <Input 
                  placeholder="Zona/Barrio" 
                  className="h-10 rounded-xl"
                  value={zoneSearch}
                  onChange={(e) => setZoneSearch(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <Input 
                  placeholder="Min $" 
                  type="number"
                  className="h-10 rounded-xl"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                />
                <Input 
                  placeholder="Max $" 
                  type="number"
                  className="h-10 rounded-xl"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                />
              </div>

              <select 
                className="h-10 text-sm bg-background border rounded-xl px-4 outline-none"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value as any)}
              >
                <option value="all">Todo tipo</option>
                <option value="products">Solo Productos</option>
                <option value="services">Solo Servicios</option>
              </select>
            </>
          )}
        </div>
      </div>

      <Separator className="mb-8" />

      {loading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : sortedAndFilteredShops.length === 0 ? (
        <div className="p-24 border rounded-3xl border-dashed text-center bg-muted/30">
          <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
          <p className="text-xl font-medium text-muted-foreground">No encontramos tiendas con esos filtros.</p>
          <Button 
            variant="link" 
            onClick={() => {
              setShopNameSearch("");
              setCategoryFilter(null);
              setZoneFilter(null);
              setPriceRange({ min: "", max: "" });
              setBusinessType("all");
              setSelectedLocation(null);
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {sortedAndFilteredShops.map((shop) => (
            <ShopCard key={shop.id} shop={shop} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsSnap, shopsSnap] = await Promise.all([
          getDocs(collection(firestoreDb, "products")),
          getDocs(collection(firestoreDb, "shops"))
        ]);
        
        setProducts(productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product)));
        setShops(shopsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Shop)));
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const sortedProducts = useMemo(() => {
    const shopMap = new Map<string, Shop>(shops.map(s => [s.id, s]));
    
    return [...products].sort((a, b) => {
      const shopA = shopMap.get(a.shopId);
      const shopB = shopMap.get(b.shopId);
      
      const planA = shopA?.plan || "free";
      const planB = shopB?.plan || "free";
      
      if (planA === "premium" && planB !== "premium") return -1;
      if (planB === "premium" && planA !== "premium") return 1;
      return 0;
    });
  }, [products, shops]);

  return (
    <div className="container py-12 px-4 md:px-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Todos los Productos</h1>
          <p className="text-muted-foreground">Explora el catálogo completo de nuestra comunidad.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : products.length === 0 ? (
        <div className="p-12 border rounded-lg border-dashed text-center">
          <p className="text-muted-foreground">Aún no hay productos registrados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {sortedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ServicesPage() {
  const [services, setServices] = useState<Product[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<{ continent: string; country: string; city: string } | null>(null);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsSnap, shopsSnap] = await Promise.all([
          getDocs(query(collection(firestoreDb, "products"), where("type", "==", "service"))),
          getDocs(collection(firestoreDb, "shops"))
        ]);
        
        setServices(productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product)));
        setShops(shopsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Shop)));
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const availableLocations = useMemo(() => {
    const locations: Record<string, { country: string; continent: string; cities: Set<string> }> = {};
    
    shops.forEach(shop => {
      const country = normalizeCountry(shop.location.country);
      const city = normalizeCity(shop.location.city);
      const continent = getContinentForCountry(country);
      
      if (!locations[country]) {
        locations[country] = { country, continent, cities: new Set() };
      }
      locations[country].cities.add(city);
    });
    
    return Object.values(locations).map(loc => ({
      ...loc,
      cities: Array.from(loc.cities)
    }));
  }, [shops]);

  const shopMap = useMemo(() => new Map<string, Shop>(shops.map(s => [s.id, s])), [shops]);

  const sortedAndFilteredServices = useMemo(() => {
    let result = [...services];

    // Filter by location
    if (selectedLocation) {
      result = result.filter(service => {
        const shop = shopMap.get(service.shopId);
        if (!shop) return false;
        
        const shopCountry = normalizeCountry(shop.location.country);
        const countryMatch = !selectedLocation.country || shopCountry === selectedLocation.country;
        
        const shopCity = normalizeCity(shop.location.city);
        const cityMatch = !selectedLocation.city || 
                          selectedLocation.city === "Todas las ciudades" || 
                          shopCity === selectedLocation.city;
        
        return countryMatch && cityMatch;
      });
    }

    // Category filter
    if (categoryFilter) {
      result = result.filter(s => s.category === categoryFilter);
    }

    // Name search
    if (serviceSearch) {
      result = result.filter(s => s.name.toLowerCase().includes(serviceSearch.toLowerCase()));
    }

    // Price filtering
    if (priceRange.min || priceRange.max) {
      result = result.filter(p => {
        const min = priceRange.min ? parseFloat(priceRange.min) : 0;
        const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
        return p.price >= min && p.price <= max;
      });
    }

    // PREMIUM PRIORITIZATION
    return result.sort((a, b) => {
      const planA = shopMap.get(a.shopId)?.plan || "free";
      const planB = shopMap.get(b.shopId)?.plan || "free";
      
      if (planA === "premium" && planB !== "premium") return -1;
      if (planB === "premium" && planA !== "premium") return 1;
      return 0;
    });
  }, [services, selectedLocation, categoryFilter, serviceSearch, priceRange, shopMap]);

  return (
    <div className="container py-12 px-4 md:px-8">
      <LocationSelector 
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onSelect={(loc) => setSelectedLocation(loc)}
        availableLocations={availableLocations}
      />

      <div className="flex flex-col gap-8 mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Servicios Profesionales</h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {selectedLocation ? (
                <span>
                  {selectedLocation.city === "Todas las ciudades" ? "" : `${selectedLocation.city}, `}
                  {selectedLocation.country}
                </span>
              ) : (
                <span>Todas las ubicaciones</span>
              )}
              <Button variant="link" size="sm" className="h-auto p-0 text-primary" onClick={() => setIsLocationModalOpen(true)}>
                Cambiar
              </Button>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full md:w-auto gap-2"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtros {showAdvancedFilters ? "Básicos" : "Avanzados"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar servicio..." 
              className="pl-10 h-10 rounded-xl"
              value={serviceSearch}
              onChange={(e) => setServiceSearch(e.target.value)}
            />
          </div>

          <select 
            className="h-10 text-sm bg-background border rounded-xl px-4 outline-none w-full"
            onChange={(e) => setCategoryFilter(e.target.value || null)}
            value={categoryFilter || ""}
          >
            <option value="">Todas las categorías</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {showAdvancedFilters && (
            <div className="flex gap-2">
              <Input 
                placeholder="Escala Min $" 
                type="number"
                className="h-10 rounded-xl"
                value={priceRange.min}
                onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
              />
              <Input 
                placeholder="Escala Max $" 
                type="number"
                className="h-10 rounded-xl"
                value={priceRange.max}
                onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
              />
            </div>
          )}
        </div>
      </div>

      <Separator className="mb-8" />

      {loading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : sortedAndFilteredServices.length === 0 ? (
        <div className="p-24 border rounded-3xl border-dashed text-center bg-muted/30">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
          <p className="text-xl font-medium text-muted-foreground">No encontramos servicios con esos filtros.</p>
          <Button 
            variant="link" 
            onClick={() => {
              setServiceSearch("");
              setCategoryFilter(null);
              setPriceRange({ min: "", max: "" });
              setSelectedLocation(null);
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {sortedAndFilteredServices.map((service) => (
            <ProductCard key={service.id} product={service} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ProfilePage() {
  return (
    <div className="container py-12">
      <h1 className="text-3xl font-bold mb-8">Mi Perfil</h1>
      <div className="max-w-2xl border rounded-lg p-8">
        <p className="text-muted-foreground">Aquí podrás gestionar tus datos personales y preferencias.</p>
      </div>
    </div>
  );
}
